import { ListLayout } from "@ekafyi/viteui-lib/components";
import type { ReactNode, Dispatch, SetStateAction } from "react";
import { createContext, useEffect, useState, useContext, useMemo } from "react"; // Suspense
import { useInView } from "react-intersection-observer";
import { usePosts } from "@/lib/client-queries";

type TNoteContext = {
  data: NoteSectionLayoutProps["initialData"];
  setData?: Dispatch<SetStateAction<NoteSectionLayoutProps["initialData"]>>;
};

interface NoteSectionLayoutProps {
  activeSlug?: string;
  children: ReactNode;
  initialData?: { title: string; slug: string }[];
}

const NoteContext = createContext<TNoteContext>({ data: [] });

const NoteSectionLayout = ({
  activeSlug = "",
  children,
  initialData = [],
}: NoteSectionLayoutProps) => {
  useEffect(() => { console.log("Layout mounts") }, []); // prettier-ignore

  const { ref, inView } = useInView({ triggerOnce: true });

  const [data, setData] = useState<NoteSectionLayoutProps["initialData"]>(
    initialData?.length ? initialData : undefined
  );
  const memoData = useMemo(() => ({ data, setData }), [data]);

  return (
    <NoteContext.Provider value={memoData}>
      <ListLayout
        ref={ref}
        activeSlug={activeSlug}
        listNode={!data?.length && inView ? <ClientFetcher /> : null}
        data={data}
        inView={inView}
        section={{ title: "Eka’s Notes", path: "notes" }}
      >
        {children}
      </ListLayout>
    </NoteContext.Provider>
  );
};

const ClientFetcher = () => {
  useEffect(() => { console.log("Fetcher mounts") }, []); // prettier-ignore
  const { setData } = useContext(NoteContext);
  const { data: posts, isLoading, isError } = usePosts();

  // Update context data in useEffect to prevent "Cannot update a component" warning:
  // https://flaviocopes.com/react-update-while-rendering-different-component/
  useEffect(() => {
    if (posts?.length && typeof setData !== "undefined") {
      setData(posts);
    }
  }, [posts, isLoading, setData]);

  if (isLoading || isError) {
    return (
      <div
        className="flex items-center justify-center p-8 text-sm"
        aria-live="polite"
        aria-busy={isLoading}
        role="status"
      >
        <span>{isLoading ? `Loading...` : `Failed fetching data. Please reload.`}</span>
      </div>
    );
  }
  return null;
};

export default NoteSectionLayout;
